import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectLccaTypeData, selectLccaTypeTotal } from '../store/common.selector';
import { CatalogLccaTypeResponse } from '../../shared/models/common/lcca-type-model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'lcca-type-list',
  templateUrl: './lcca-type-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./lcca-type-list.component.scss']
})
export class LccaTypeListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  lccaTypes = toSignal(this.store$.select(selectLccaTypeData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectLccaTypeTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find LCCA Types')), {initialValue: false});

  
  readonly auditResourceName = 'Lcca Type';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedLccaTypes = signal<CatalogLccaTypeResponse[]>([]);

  // Add LCCA Type Form Variables
  dialogVisible = signal(false);
  lccaTypeName = signal('');
  savingLccaType = signal(false);
  isEditMode = signal(false);
  editingLccaTypeId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedLccaTypes().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createLccaTypeSuccess, action.updateLccaTypeSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaType.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteLccaTypeSuccess, action.bulkDeleteLccaTypesSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedLccaTypes.set([]);
      this.loadLccaTypes(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createLccaTypeError, action.updateLccaTypeError, action.deleteLccaTypeError, action.bulkDeleteLccaTypesError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaType.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findLccaTypes({
      page,
      size,
      keywords
    }));
  }

  loadLccaTypes(): void {
    this.store$.dispatch(action.findLccaTypes({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.lccaTypeName.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingLccaTypeId.set(null);
  }

  closeDialog(): void {
    this.lccaTypeName.set('');
    this.savingLccaType.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingLccaTypeId.set(null);
  }

  saveLccaType(): void {
    if(!this.lccaTypeName().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'LCCA type name is required'
      });
      return;
    }

    this.savingLccaType.set(true);

    const lccaTypeData = {
      name: this.lccaTypeName().trim()
    };

    if(this.isEditMode() && this.editingLccaTypeId()) {
      this.store$.dispatch(action.updateLccaType({ id: this.editingLccaTypeId()!, lccaType: lccaTypeData }))
    } else {
      this.store$.dispatch(action.createLccaType({ lccaType: lccaTypeData }))
    }
  }

  editLccaType(): void {
    if (this.selectedLccaTypes().length === 1) {
      const lccaTypeToEdit = this.selectedLccaTypes()[0];
      this.lccaTypeName.set(lccaTypeToEdit.name ? lccaTypeToEdit.name : '');
      this.isEditMode.set(true);
      this.editingLccaTypeId.set(lccaTypeToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one LCCA type to edit'
      });
    }
  }

  deleteLccaType(): void {
    if (this.selectedLccaTypes().length === 0) {
      return;
    }

    const lccaTypesToDelete = [...this.selectedLccaTypes()];
    const count = lccaTypesToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${lccaTypesToDelete[0].name}"?`;
    } else {
      const maxDisplay = 10;
      const namesToShow = lccaTypesToDelete.slice(0, maxDisplay);
      const namesList = namesToShow.map((t, index) => `${index + 1}. ${t.name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} LCCA types?<br><br><strong>Names to be deleted:</strong><br>${namesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteLccaType({ id: lccaTypesToDelete[0].id }));
        } else {
          const ids = lccaTypesToDelete.map(t => t.id);
          this.store$.dispatch(action.bulkDeleteLccaTypes({ ids }));
        }
      }
    });
  }

  refreshLccaTypes(): void {
    this.selectedLccaTypes.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'LCCA types list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedLccaTypes();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.type_name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}

